import type { PairedModifierGroupMap, ProductModifierGroups } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds bidirectional name maps between paired included and extras groups.
 *
 * - `includedModifierGroupsByExtrasName`: extras group name → included group name
 * - `extrasModifierGroupsByIncludedName`: included group name → extras group name
 */
export function generatePairedModifierGroupMaps(
  modifierGroups: ProductModifierGroups,
): PairedModifierGroupMaps {
  const groupsById = new Map(modifierGroups.map((group) => [group.id, group]));

  return modifierGroups.reduce<PairedModifierGroupMaps>(
    (maps, group) => {
      if (group.groupRole !== 'extras' || !group.pairedGroupId) return maps;

      const includedGroup = groupsById.get(group.pairedGroupId);

      if (!includedGroup) return maps;

      return {
        includedModifierGroupsByExtrasName: {
          ...maps.includedModifierGroupsByExtrasName,
          [group.name]: includedGroup.name,
        },
        extrasModifierGroupsByIncludedName: {
          ...maps.extrasModifierGroupsByIncludedName,
          [includedGroup.name]: group.name,
        },
      };
    },
    { includedModifierGroupsByExtrasName: {}, extrasModifierGroupsByIncludedName: {} },
  );
}

// ─── Types ──────────────────────────────────────────────────────────────────

type PairedModifierGroupMaps = {
  includedModifierGroupsByExtrasName: PairedModifierGroupMap;
  extrasModifierGroupsByIncludedName: PairedModifierGroupMap;
};
